import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, Save, Activity, Heart, Thermometer, Weight, Droplets, Wind, Ruler, Search } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MedicineSearch from "@/components/MedicineSearch";
import { useAuth } from "@/context/AuthContext";

const FREQUENCIES = ["1-0-1", "1-1-1", "1-0-0", "0-0-1", "0-1-0", "1-1-0", "Twice daily", "Once daily", "Once a week", "SOS"];
const TIMINGS = ["After food", "Before food", "With food", "Empty stomach", "At bedtime", "As directed"];

const LANGS = [
    { code: "English", label: "EN" },
    { code: "Hindi", label: "हिंदी" },
    { code: "Gujarati", label: "ગુજરાતી" },
];

const TIMINGS_L = {
    English: TIMINGS,
    Hindi: ["खाने के बाद", "खाने से पहले", "खाने के साथ", "खाली पेट", "सोने से पहले", "जैसे बताया गया"],
    Gujarati: ["જમ્યા પછી", "જમતા પહેલાં", "જમવા સાથે", "ખાલી પેટે", "સૂતા પહેલાં", "બતાવ્યા પ્રમાણે"],
};

const DURATIONS_L = {
    English: ["2 days", "3 days", "5 days", "7 days", "10 days", "15 days", "1 month", "2 months", "3 months", "Continue"],
    Hindi: ["2 दिन", "3 दिन", "5 दिन", "7 दिन", "10 दिन", "15 दिन", "1 महीना", "2 महीने", "3 महीने", "जारी रखें"],
    Gujarati: ["2 દિવસ", "3 દિવસ", "5 દિવસ", "7 દિવસ", "10 દિવસ", "15 દિવસ", "1 મહિનો", "2 મહિના", "3 મહિના", "ચાલુ રાખો"],
};

const FOLLOWUP_L = {
    English: [["After 3 days", 3], ["After 1 week", 7], ["After 15 days", 15], ["After 1 month", 30], ["After 3 months", 90], ["Next menstrual cycle", null]],
    Hindi: [["3 दिन बाद", 3], ["1 सप्ताह बाद", 7], ["15 दिन बाद", 15], ["1 महीने बाद", 30], ["3 महीने बाद", 90], ["अगले मासिक चक्र में", null]],
    Gujarati: [["3 દિવસ પછી", 3], ["1 અઠવાડિયા પછી", 7], ["15 દિવસ પછી", 15], ["1 મહિના પછી", 30], ["3 મહિના પછી", 90], ["આવતા માસિક ચક્રમાં", null]],
};

const VITAL_FIELDS = [
    { key: "bp", label: "BP", unit: "mmHg", placeholder: "120/80", icon: Activity },
    { key: "pulse", label: "Pulse", unit: "bpm", placeholder: "72", icon: Heart },
    { key: "spo2", label: "SpO2", unit: "%", placeholder: "98", icon: Wind },
    { key: "temperature", label: "Temp", unit: "°F", placeholder: "98.6", icon: Thermometer },
    { key: "weight", label: "Weight", unit: "kg", placeholder: "65", icon: Weight },
    { key: "height", label: "Height", unit: "cm", placeholder: "155", icon: Ruler },
    { key: "rbs", label: "RBS", unit: "mg/dL", placeholder: "110", icon: Droplets },
];

export default function NewVisit() {
    const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const [doctors, setDoctors] = useState([]);
    const [prescriberId, setPrescriberId] = useState("");
    const [includeSig, setIncludeSig] = useState(true);
    const [patient, setPatient] = useState(null);
    const [symptoms, setSymptoms] = useState("");
    const [diagnosis, setDiagnosis] = useState("");
    const [notes, setNotes] = useState("");
    const [advice, setAdvice] = useState("");
    const [followUp, setFollowUp] = useState("");
    const [vitals, setVitals] = useState({ bp: "", pulse: "", spo2: "", temperature: "", weight: "", height: "", rbs: "" });
    const [meds, setMeds] = useState([]);
    const [saving, setSaving] = useState(false);
    const [lang, setLang] = useState("English");
    const [rxTab, setRxTab] = useState("disease");
    const [diseaseQ, setDiseaseQ] = useState("");
    const [diseaseResults, setDiseaseResults] = useState([]);
    const [followUpText, setFollowUpText] = useState("");
    const [soapMode, setSoapMode] = useState(false);
    const [icdResults, setIcdResults] = useState([]);
    const [anc, setAnc] = useState({ fundal_height: "", fhr: "", fetal_movement: "", presentation: "" });

    const gaText = (lmp) => {
        if (!lmp) return "";
        const days = Math.floor((Date.now() - new Date(lmp)) / 86400000);
        return days >= 0 ? `${Math.floor(days / 7)}w${days % 7}d` : "";
    };

    const allergyHits = (m) => {
        const tokens = (patient?.allergies || "").split(/[,\s;/]+/).map((t) => t.trim().toLowerCase()).filter((t) => t.length > 2);
        const blob = `${m.name} ${m.generic || ""}`.toLowerCase();
        return tokens.filter((t) => blob.includes(t));
    };

    useEffect(() => {
        api.get(`/patients/${id}`).then(({ data }) => {
            setPatient(data);
            if (data.preferred_language && data.preferred_language !== "English") setLang(data.preferred_language);
        }).catch((e) => { toast.error(fmtErr(e)); navigate("/patients"); });
        api.get("/team").then(({ data }) => {
            setDoctors(data);
            if (data.length && !data.some((d) => d.has_signature)) setIncludeSig(false);
        }).catch(() => {});
    }, [id]);

    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/diseases", { params: { q: diseaseQ } });
                setDiseaseResults(data);
            } catch { setDiseaseResults([]); }
        }, 200);
        return () => clearTimeout(t);
    }, [diseaseQ]);

    useEffect(() => {
        if (!diagnosis || diagnosis.length < 2 || diagnosis.includes("]")) { setIcdResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/icd", { params: { q: diagnosis } });
                setIcdResults(data);
            } catch { setIcdResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [diagnosis]);

    const applyDisease = (d) => {
        setDiagnosis(d.name);
        const flagged = d.medicines.map((m) => ({ ...m, _allergy: allergyHits(m).length > 0 }));
        const hits = flagged.filter((m) => m._allergy);
        setMeds([...meds, ...flagged]);
        setDiseaseQ("");
        setDiseaseResults([]);
        toast.success(`${d.medicines.length} medicine${d.medicines.length === 1 ? "" : "s"} added for ${d.name}`);
        hits.forEach((m) => toast.warning(`Allergy alert: ${m.name} may conflict with the patient's recorded allergies`, { duration: 6000 }));
    };

    const addMed = (m) => {
        const hits = allergyHits(m);
        if (hits.length) toast.warning(`Allergy alert: ${m.name} may conflict with recorded allergy (${hits.join(", ")})`, { duration: 6000 });
        setMeds([...meds, { name: m.name, generic: m.generic || "", form: m.form || "", strength: m.strength || "", dosage: "1", frequency: "1-0-1", timing: TIMINGS_L[lang][0], duration: DURATIONS_L[lang][1], _allergy: hits.length > 0 }]);
    };

    const setMed = (i, k, v) => setMeds(meds.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));

    const [rxAlerts, setRxAlerts] = useState([]);
    useEffect(() => {
        if (!meds.length) { setRxAlerts([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/prescriptions/check", { medicines: meds });
                setRxAlerts(data.alerts || []);
            } catch { setRxAlerts([]); }
        }, 400);
        return () => clearTimeout(t);
    }, [meds]);

    const save = async () => {
        if (!symptoms.trim() && !diagnosis.trim()) { toast.error("Add at least symptoms or a diagnosis"); return; }
        setSaving(true);
        try {
            const { data } = await api.post(`/patients/${id}/visits`, {
                symptoms, diagnosis, notes, advice, follow_up: followUp, follow_up_text: followUpText, vitals, medicines: meds,
                anc,
                prescribing_doctor_id: prescriberId || user?.id,
                include_signature: includeSig && !!doctors.find((d) => d.id === (prescriberId || user?.id))?.has_signature,
            });
            toast.success("Consultation saved");
            navigate(`/visits/${data.id}`);
        } catch (e) {
            toast.error(fmtErr(e));
            setSaving(false);
        }
    };

    if (!patient) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" /></div>;

    return (
        <div className="space-y-5 animate-fade-up" data-testid="new-visit-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button onClick={() => navigate(`/patients/${id}`)} data-testid="back-to-patient-btn" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#E06D1F]">
                    <ArrowLeft className="h-4 w-4" /> {patient.name} <span className="font-mono2 text-xs">({patient.patient_uid})</span>
                </button>
                <Button data-testid="save-visit-btn" onClick={save} disabled={saving} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Consultation
                </Button>
            </div>

            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid="prescriber-bar">
                <span className="text-sm font-medium text-slate-600">Prescribing Doctor:</span>
                <select data-testid="prescriber-select" value={prescriberId || user?.id || ""} onChange={(e) => setPrescriberId(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                    {doctors.map((d) => <option key={d.id} value={d.id}>{d.name?.toLowerCase().startsWith("dr") ? d.name : `Dr. ${d.name}`}{d.specialization ? ` — ${d.specialization}` : ""}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="include-signature-label">
                    <input
                        type="checkbox"
                        data-testid="include-signature-checkbox"
                        checked={includeSig && !!doctors.find((d) => d.id === (prescriberId || user?.id))?.has_signature}
                        disabled={!doctors.find((d) => d.id === (prescriberId || user?.id))?.has_signature}
                        onChange={(e) => setIncludeSig(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 accent-[#E06D1F]"
                    />
                    Include digital signature
                    {!doctors.find((d) => d.id === (prescriberId || user?.id))?.has_signature && <span className="text-xs text-slate-400">(no signature on file — add in Team tab)</span>}
                </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[35%_1fr] gap-4 items-start">
                <div className="space-y-4">
                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="vitals-card">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-heading font-semibold text-slate-900">Vitals</h3>
                            <button type="button" data-testid="soap-mode-toggle" onClick={() => setSoapMode(!soapMode)} className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors duration-150 ${soapMode ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}>SOAP Mode {soapMode ? "ON" : "OFF"}</button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {VITAL_FIELDS.map((v) => (
                                <div key={v.key} className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
                                    <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-400"><v.icon className="h-3 w-3" />{v.label} <span className="normal-case tracking-normal">({v.unit})</span></p>
                                    <input
                                        data-testid={`vital-${v.key}-input`}
                                        value={vitals[v.key]}
                                        onChange={(e) => setVitals({ ...vitals, [v.key]: e.target.value })}
                                        placeholder={v.placeholder}
                                        className="mt-1 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-300 placeholder:font-normal"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {patient?.lmp && (
                        <div className="rounded-xl border border-orange-200 bg-[#FFF4EC]/50 p-5 shadow-sm" data-testid="anc-fields">
                            <h3 className="font-heading font-semibold text-slate-900 mb-3">ANC <span className="text-xs font-normal text-[#C2410C]">· GA {gaText(patient.lmp)}</span></h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Fundal Height (cm)</p>
                                    <input data-testid="anc-fundal-input" value={anc.fundal_height} onChange={(e) => setAnc({ ...anc, fundal_height: e.target.value })} placeholder="24" className="mt-1 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-300 placeholder:font-normal" />
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">FHR (/min)</p>
                                    <input data-testid="anc-fhr-input" value={anc.fhr} onChange={(e) => setAnc({ ...anc, fhr: e.target.value })} placeholder="140" className="mt-1 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-300 placeholder:font-normal" />
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Fetal Movement</p>
                                    <select data-testid="anc-movement-select" value={anc.fetal_movement} onChange={(e) => setAnc({ ...anc, fetal_movement: e.target.value })} className="mt-1 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none">
                                        <option value="">—</option>
                                        <option value="Good">Good</option>
                                        <option value="Reduced">Reduced</option>
                                        <option value="Absent">Absent</option>
                                    </select>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Presentation</p>
                                    <select data-testid="anc-presentation-select" value={anc.presentation} onChange={(e) => setAnc({ ...anc, presentation: e.target.value })} className="mt-1 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none">
                                        <option value="">—</option>
                                        <option value="Cephalic">Cephalic</option>
                                        <option value="Breech">Breech</option>
                                        <option value="Transverse">Transverse</option>
                                        <option value="Oblique">Oblique</option>
                                        <option value="Not assessed">Not assessed</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="complaints-card">
                        <div className="space-y-1.5">
                            <Label className="text-slate-700 font-semibold">{soapMode ? "S — Subjective (Chief complaints)" : "Chief Complaints / Symptoms"}</Label>
                            <Textarea data-testid="symptoms-input" rows={3} value={symptoms} onChange={(e) => setSymptoms(e.target.value)} placeholder="Fever x 3 days, body ache, sore throat..." />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-700 font-semibold">{soapMode ? "O — Objective (Examination findings)" : "Clinical Notes"}</Label>
                            <Textarea data-testid="notes-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="On examination: throat congested, no lymphadenopathy..." />
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="diagnosis-card">
                        <Label className="text-slate-700 font-semibold">{soapMode ? "A — Assessment (Diagnosis + ICD-10)" : "Diagnosis"}</Label>
                        <div className="relative">
                            <Input data-testid="diagnosis-input" className="mt-1.5" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="e.g. Acute upper respiratory infection (ICD suggests as you type)" />
                            {icdResults.length > 0 && (
                                <div data-testid="icd-results-dropdown" className="absolute z-20 mt-1.5 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg animate-fade-up">
                                    {icdResults.map((c) => (
                                        <button key={c.id} type="button" data-testid={`icd-option-${c.code.replace(".", "-")}`} onClick={() => { setDiagnosis(diagnosis.includes("[") ? diagnosis : `${diagnosis} [${c.code}]`); setIcdResults([]); }} className="w-full text-left px-4 py-2 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0 transition-colors duration-100">
                                            <span className="font-mono2 text-xs font-semibold text-[#C2410C]">{c.code}</span>
                                            <span className="ml-2 text-sm text-slate-700">{c.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="prescription-builder-card">
                        <h3 className="font-heading font-semibold text-slate-900 mb-3">Prescription (Rx)</h3>
                        <div className="flex flex-wrap items-center gap-1.5 mb-3">
                            {[["disease", "By Disease"], ["medicine", "Search Medicine"]].map(([k, l]) => (
                                <button key={k} type="button" data-testid={`rx-tab-${k}`} onClick={() => setRxTab(k)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${rxTab === k ? "bg-[#E06D1F] text-white border-[#E06D1F]" : "bg-white text-slate-600 border-slate-300 hover:border-orange-300"}`}>{l}</button>
                            ))}
                            <div className="ml-auto flex gap-1" data-testid="rx-language-tabs">
                                {LANGS.map((l) => (
                                    <button key={l.code} type="button" data-testid={`rx-lang-${l.code.toLowerCase()}`} onClick={() => setLang(l.code)} className={`rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${lang === l.code ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>{l.label}</button>
                                ))}
                            </div>
                        </div>
                        {rxTab === "disease" ? (
                            <div className="relative" data-testid="disease-search">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <input
                                    data-testid="disease-autocomplete-input"
                                    value={diseaseQ}
                                    onChange={(e) => setDiseaseQ(e.target.value)}
                                    placeholder="Type disease — PCOS, dysmenorrhea, UTI, ANC... meds auto-fill"
                                    className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none"
                                />
                                {diseaseResults.length > 0 && (
                                    <div data-testid="disease-results-dropdown" className="absolute z-20 mt-1.5 w-full max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg animate-fade-up">
                                        {diseaseResults.map((d) => (
                                            <button key={d.id} type="button" data-testid={`disease-option-${d.id}`} onClick={() => applyDisease(d)} className="w-full text-left px-4 py-2.5 hover:bg-[#FFF4EC] border-b border-slate-100 last:border-0 transition-colors duration-100">
                                                <p className="text-sm font-semibold text-slate-900">{d.name}</p>
                                                <p className="text-xs text-slate-500">{d.medicines.length} medicine{d.medicines.length === 1 ? "" : "s"}: {d.medicines.map((m) => m.name).join(", ")}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <MedicineSearch onSelect={addMed} />
                        )}
                        {rxAlerts.length > 0 && (
                            <div className="mt-3 space-y-1.5" data-testid="rx-safety-alerts">
                                {rxAlerts.map((a, i) => (
                                    <p key={i} data-testid={`rx-alert-${i}`} className={`rounded-lg border px-3 py-2 text-xs font-medium ${a.type === "duplicate" ? "bg-red-50 border-red-200 text-red-700" : a.type === "dosage" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-blue-50 border-blue-200 text-blue-700"}`}>{a.message}</p>
                                ))}
                            </div>
                        )}
                        {meds.length > 0 && (
                            <div className="mt-4 space-y-3" data-testid="prescription-items">
                                {meds.map((m, i) => (
                                    <div key={i} data-testid={`prescription-item-${i}`} className={`rounded-lg border p-3.5 space-y-2.5 ${m._allergy ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">{i + 1}. {m.form} {m.name} {m.strength && <span className="font-normal text-slate-500">({m.strength})</span>}
                                                    {m._allergy && <span className="ml-2 rounded bg-red-100 border border-red-300 px-1.5 py-0.5 text-[10px] font-bold text-red-700" data-testid={`allergy-badge-${i}`}>ALLERGY RISK</span>}
                                                </p>
                                                {m.generic && <p className="text-xs text-slate-500">{m.generic}</p>}
                                            </div>
                                            <button data-testid={`remove-med-${i}`} onClick={() => setMeds(meds.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Dosage</p>
                                                <input data-testid={`med-dosage-${i}`} value={m.dosage} onChange={(e) => setMed(i, "dosage", e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-emerald-600" placeholder="1 tab" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Frequency</p>
                                                <select data-testid={`med-frequency-${i}`} value={m.frequency} onChange={(e) => setMed(i, "frequency", e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-emerald-600 bg-white">
                                                    {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Timing</p>
                                                <select data-testid={`med-timing-${i}`} value={m.timing} onChange={(e) => setMed(i, "timing", e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-orange-500 bg-white">
                                                    {!TIMINGS_L[lang].includes(m.timing) && m.timing && <option value={m.timing}>{m.timing}</option>}
                                                    {TIMINGS_L[lang].map((t) => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Duration</p>
                                                <input data-testid={`med-duration-${i}`} list={`dur-opts-${i}`} value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-orange-500" placeholder={DURATIONS_L[lang][1]} />
                                                <datalist id={`dur-opts-${i}`}>{DURATIONS_L[lang].map((d) => <option key={d} value={d} />)}</datalist>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="advice-card">
                        <div className="space-y-1.5">
                            <Label className="text-slate-700 font-semibold">{soapMode ? "P — Plan (Advice / Treatment)" : "Advice / Diet"}</Label>
                            <Textarea data-testid="advice-input" rows={2} value={advice} onChange={(e) => setAdvice(e.target.value)} placeholder="Plenty of fluids, rest, steam inhalation..." />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-700 font-semibold">Follow-up <span className="text-xs font-normal text-slate-400">(patient language: {lang})</span></Label>
                            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="followup-presets">
                                {FOLLOWUP_L[lang].map(([label, days]) => (
                                    <button
                                        key={label}
                                        type="button"
                                        data-testid={`followup-preset-${days ?? "cycle"}`}
                                        onClick={() => {
                                            setFollowUpText(label);
                                            if (days) {
                                                const d = new Date();
                                                d.setDate(d.getDate() + days);
                                                setFollowUp(d.toISOString().slice(0, 10));
                                            } else {
                                                setFollowUp("");
                                            }
                                        }}
                                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-150 ${followUpText === label ? "bg-[#E06D1F] text-white border-[#E06D1F]" : "bg-white text-slate-600 border-slate-300 hover:border-orange-300"}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div className="max-w-xs">
                                <Input data-testid="follow-up-input" type="date" value={followUp} onChange={(e) => { setFollowUp(e.target.value); setFollowUpText(""); }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
